import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // Silence 401 on bootstrap (unauthenticated landing)
      const { data } = await api.get("/auth/me", { validateStatus: (s) => s < 500 });
      if (data && data.user_id) {
        setUser(data);
        return data;
      }
      setUser(null);
      return null;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.debug("auth/me bootstrap failed", e?.message);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Emergent OAuth callback, skip /me — AuthCallback handles it.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Token persisted in localStorage as a deliberate fallback for WS auth
    // (?token=) and cross-origin bearer requests where the httpOnly cookie
    // (the PRIMARY auth path) cannot be attached. The cookie remains the
    // canonical authority server-side.
    if (data?.token) localStorage.setItem("tf_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    if (data?.token) localStorage.setItem("tf_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) {
      // eslint-disable-next-line no-console
      console.debug("logout request failed (continuing local logout)", e?.message);
    }
    localStorage.removeItem("tf_token");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, login, register, logout, refresh }),
    [user, loading, login, register, logout, refresh]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
